import { Suspense, lazy } from "react";
import { createFileRoute, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";

import { Spinner } from "@/components/ui/spinner";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { RequirePermission } from "@/components/portal/RequirePermission";

import { spacesApi } from "@/services/api/spaces";

// Lazy-load the editor chunk (shared with the /new route; Vite dedupes it).
const SpaceEditor = lazy(() =>
  import("@/components/portal/spaces/SpaceEditor").then((m) => ({
    default: m.SpaceEditor,
  })),
);

export const Route = createFileRoute("/portal/spaces/$spaceId/edit")({
  component: EditSpacePage,
});

function EditSpacePage() {
  const { spaceId } = useParams({ from: "/portal/spaces/$spaceId/edit" });
  const { token } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const orgId = selectedOrganisation?.organisationId ?? "";

  // orgId-scoped key — matches the view route + SpaceEditor's post-save
  // removeQueries(["space", orgId, slug]) so an edit-save evicts the same
  // entry the view reads.
  const space = useQuery({
    queryKey: ["space", orgId, spaceId],
    queryFn: () => spacesApi.getSpace(token!, orgId, spaceId),
    enabled: !!token && !!orgId && !!spaceId,
  });

  if (space.isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (space.error || !space.data) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
        {space.error instanceof Error ? space.error.message : "Failed to load space"}
      </div>
    );
  }

  return (
    <RequirePermission
      resource="spaces"
      action="edit"
      backTo={`/portal/spaces/${spaceId}`}
      backLabel="Back to space"
    >
      <Helmet>
        <title>Edit {space.data.name}</title>
      </Helmet>
      <Suspense
        fallback={
          <div className="flex items-center justify-center py-12">
            <Spinner />
          </div>
        }
      >
        <SpaceEditor initial={space.data} />
      </Suspense>
    </RequirePermission>
  );
}
